// src/components/ElectronAuthPrompt.jsx
import React, { useState, useEffect } from "react";
import { AppIcon, AppText } from "@toothfairy/shared-ui";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
// --- IMPORT YOUR LoadingPlaceholder ---
// Make sure the path is correct for your project structure
import LoadingPlaceholder from "../LoadingPlaceholder"; // Adjust path if necessary
import ToothFairyButton from "../ToothFairyComponents/ToothFairyButton";
// Example placeholder definition if LoadingPlaceholder isn't imported yet:
// const LoadingPlaceholder = ({ caption = "", style, brandedAnimation = true, ...props }) => (
//   <div style={{ ...style, display: 'inline-flex', alignItems: 'center', flexDirection: 'column', padding: '10px', border: '1px dashed lightgray', borderRadius: '4px', minWidth: '80px' }} {...props}>
//     {brandedAnimation && <div style={{width: '20px', height: '20px', background: '#007bff', borderRadius: '50%', marginBottom: '5px', animation: 'spin 1s linear infinite'}}><style>{`@keyframes spin { 100% { transform: rotate(360deg); } }`}</style></div>}
//     <span style={{ fontSize: '12px', color: '#555' }}>{caption || 'Loading...'}</span>
//   </div>
// );
// ----------------------------------------

const ElectronAuthPrompt = ({ appLogo }) => {
  const { currentTheme } = AppTheme.useTheme();
  const [statusMessage, setStatusMessage] = useState(
    "Click below to log in using your browser."
  );
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const handleLoginClick = async () => {
    if (window.electronAPI) {
      setIsLoggingIn(true);
      setStatusMessage("Redirecting to browser for login..."); // Keep this message concise
      try {
        await window.electronAPI.login();
        setStatusMessage("Waiting for browser login completion..."); // Update message for waiting phase
        // Keep spinner active
      } catch (error) {
        console.error("Electron login initiation failed:", error);
        setStatusMessage(`Login initiation failed: ${error.message}`);
        setIsLoggingIn(false);
      }
    } else {
      setStatusMessage("Error: Electron API not available.");
      setIsLoggingIn(false);
    }
  };

  useEffect(() => {
    const handleLoginFailure = (errorMsg) => {
      setStatusMessage(`Login failed: ${errorMsg}`);
      setIsLoggingIn(false);
    };

    let removeListener = () => {};

    if (window.electronAPI?.onLoginFailure) {
      removeListener = window.electronAPI.onLoginFailure(handleLoginFailure);
    }

    return () => {
      if (typeof removeListener === "function") {
        removeListener();
      }
    };
  }, []);

  // --- Styles (mostly unchanged) ---
  const pageStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    backgroundColor: "rgba(11, 16, 25)",
    padding: "20px",
    boxSizing: "border-box",
    width: "100%",
    flexDirection: "column",
  };

  const containerStyle = {
    backgroundColor: "rgba(11, 16, 25, 0.8)",
    padding: "40px 30px",
    borderRadius: "8px",
    boxShadow: "rgb(11, 16, 25)",
    textAlign: "center",
    maxWidth: "400px",
    width: "100%",
  };

  const logoStyle = {
    maxWidth: "100px",
    height: "auto",
    marginBottom: "25px",
  };

  const logo = {
    height: 50,
    width: "auto",
    margin: "auto",
    marginBottom: 10,
    resizeMode: "contain",
  };
  const titleStyle = {
    marginBottom: "15px",
    color: "#333",
    fontSize: "24px",
    fontWeight: "600",
  };

  // Adjusted status style slightly for better alignment with LoadingPlaceholder
  const statusContainerStyle = {
    margin: "20px 0 30px 0",
    color: "#555",
    fontSize: "16px",
    minHeight: "5em", // Increase minHeight slightly to accommodate LoadingPlaceholder
    display: "flex",
    flexDirection: "column", // Stack message and loader vertically
    alignItems: "center",
    justifyContent: "center",
  };

  const statusTextStyle = {
    marginBottom: "15px", // Add space between text and loader when both are visible
  };

  const buttonStyle = {
    padding: "12px 25px",
    fontSize: "16px",
    backgroundColor: "#007bff",
    color: "white",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    transition: "background-color 0.2s ease, opacity 0.2s ease",
    width: "100%",
    boxSizing: "border-box",
    opacity: isLoggingIn ? 0.7 : 1,
  };

  const titleLabel = {
    marginBottom: 0,
    marginTop: 10,
    alignSelf: "center",
    maxWidth: "100%",
    fontWeight: "bold",
    textAlign: "center",
  };

  return (
    <div style={pageStyle}>
      <div
        className="electron_titlebar"
        style={{
          marginTop: 0,
          width: "100%",
          position: "absolute",
          top: 0,
          backgroundColor: "#2a313b",
        }}
      />
      <div style={containerStyle}>
        <img
          style={logo}
          resizeMode="contain"
          src={require("../../assets/logoTitle.png")}
        />

        <AppText
          style={{ fontSize: currentTheme?.theme?.small_font_size }}
          wrapperStyle={titleLabel}
          color={currentTheme?.theme?.white}
          fontWeight="bold"
          preventTranslation
        >
          Create powerful AI Agents tailored to your organisation, privately and
          securely.
        </AppText>
        {/* Use LoadingPlaceholder when logging in */}
        <div style={statusContainerStyle}>
          {isLoggingIn ? (
            <LoadingPlaceholder
              caption={statusMessage} // Use statusMessage as the caption for the loader
              brandedAnimation={true} // Use the branded animation
              fontColor={currentTheme?.theme?.mediumGrey}
              // Add any specific inline styles if needed, otherwise rely on its internal styles
              // style={{ marginTop: '10px' }} // Example: Add margin if needed
            />
          ) : (
            // Only show the status text when not logging in
            <AppText
              fontWeight="bold"
              color={currentTheme?.theme?.mediumGrey}
              style={{ margin: 0 }}
              preventTranslation
            >
              {statusMessage}
            </AppText>
          )}
        </div>
        <ToothFairyButton
          wrapperStyle={{
            marginTop: 20,
            zIndex: 10,
            minWidth: 200,
            marginHorizontal: "auto",
          }}
          isDisabled={isLoggingIn}
          title={isLoggingIn ? "Processing..." : "Login with browser"}
          onPress={() => {
            handleLoginClick();
          }}
        />
      </div>
    </div>
  );
};

export default ElectronAuthPrompt;
